import 'core-js/features/regexp';
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'typeface-muli';
import './react-table-defaults';
import './styles/index.css';
import './intercom';
import React from 'react';
import ReactDOM from 'react-dom';
import App from 'app/App';
import * as FullStory from '@fullstory/browser';
// import * as serviceWorker from './service-worker';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { getEnvironment, getRegion, getExpressAPIHost, setAPIHostnames, getAPIHost, getUserRegion } from './utils';
import Split from './app/split';
import scheduleMeasurement from './util-files/observability-utils';

setAPIHostnames(window.location.href);

const getAuthPackageApiHost = () => {
    const authRegion = sessionStorage.getItem('mm.auth.region') || sessionStorage.getItem('mm.region') || getUserRegion() || getRegion(window.location.hostname);
    const region = (authRegion || '').trim().toUpperCase();
    // Always update Split attribute to prevent stale state
    Split.setAttribute('region', region || 'OTHER');
    if (region && region !== 'OTHER') {
        return Split.getBooleanTreatment('use_express_auth_endpoints') ? getExpressAPIHost() : getAPIHost();
    }
    // Region unknown (e.g. new browser tab with empty sessionStorage, or cold
    // start before region-config fetch completes). Default to Express —
    // LoopBack is being deprecated and is turned off in some environments.
    return getExpressAPIHost();
};

// Expose utils on window for common_cognito_package to access
window.mmUtils = Object.freeze({
    getAPIHost: getAuthPackageApiHost,
    getRegion,
});

FullStory.init({ orgId: 'T3HTN' });

const ddService = getRegion(window.location.hostname).toLowerCase();
const environment = getEnvironment(window.location.hostname).toLowerCase();
const tracingOrigins = [getExpressAPIHost(), getAPIHost()];
const appVersion = process.env.REACT_APP_VERSION;

const replayExcludedRegions = [
    // "legacy",
    'nl',
    'demo',
    /* "tcj-erie",
    "tcj-nebraska",
    "tcj-liberty",
    "tcj-kytn",
    "tcj-gpda",
    "tcj-whiteeagle", */
];

const ddEventHandler = (event, context) => {
    if (window.location.hostname.includes('localhost')) {
        return false;
    }

    try {
        const viewUrl = new URL(event.view.url);
        const { hash } = viewUrl;
        const segments = hash.replace(/^#\/?/, '').split('/');

        let viewName = segments[0] || 'unknown';

        const specialPaths = (process.env.REACT_APP_DD_SPECIAL_PATHS || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        const extendedPaths = (process.env.REACT_APP_DD_EXTENDED_PATHS || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);

        if (specialPaths.includes(viewName) && segments[1]) {
            viewName += `-${segments[1]}`;
        }

        if (extendedPaths.includes(viewName) && segments[2]) {
            viewName += `-${segments[2]}`;
        }

        event.view.name = viewName;
    } catch (e) {
        event.view.name = 'unknown';
    }

    return true;
};

const isRegionExcluded = replayExcludedRegions.some((region) => region.toLowerCase() === ddService);
const isProduction = environment === 'production';

datadogRum.init({
    applicationId: process.env.REACT_APP_DATADOG_RUM_APPLICATION_ID,
    clientToken: process.env.REACT_APP_DATADOG_RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: ddService,
    env: environment,
    version: appVersion,
    sessionSampleRate: isProduction ? 100 : 25,
    sessionReplaySampleRate: 100,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    trackViewsManually: false,
    defaultPrivacyLevel: 'mask-user-input',
    enableExperimentalFeatures: ['clickmap'],
    beforeSend: ddEventHandler,
    allowedTracingUrls: tracingOrigins,
    startSessionReplayRecordingManually: true,
});

datadogLogs.init({
    clientToken: process.env.REACT_APP_DATADOG_RUM_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: ddService,
    env: environment,
    version: appVersion,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: 'all',
    forwardReports: 'all',
    sessionSampleRate: 100,
    beforeSend: ddEventHandler,
});

scheduleMeasurement(); // start observability measurement after DD Logs is initialized

const root = document.getElementById('root');

ReactDOM.render(<App />, root);

root.classList.add('fs-unmask');

if (process.env.NODE_ENV === 'development' && module.hot) {
    module.hot.accept('app/App', () => {
        const NextApp = require('app/App').default;
        ReactDOM.render(<NextApp />, root);
    });
}

// We have deprecated the service worker in this project, so we unregister it here to prevent any old service workers from interfering with the application.
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => registration.unregister());
    });

    if (caches) {
        caches.keys().then(async (names) => {
            await Promise.all(names.map((name) => caches.delete(name)));
        });
    }
}
